<!-- Video of all other remote participants apart from the moderator/Clinician -->
<template>
  <li
    class="relative"
    :class="[
      enlargedMode ? enlargedContainerClass : tileContainerClass,
      { 'rec-shadow': streamRecording },
    ]"
  >
    <!-- Stream Device Icon -->
    <div
      v-if="isExternalDevice || isClinicianStream"
      class="
        bg-vice-teal-600
        rounded-full
        w-10
        h-10
        content-center
        justify-center
        absolute
        top-2
        left-2
        z-10
        tooltip
      "
    >
      <div class="flex justify-center">
        <ViceExamCam v-if="userDisplayName === 'Exam Camera'" class="h-8 w-8" />
        <ViceMonitor v-if="userDisplayName === 'Monitor'" class="h-8 w-8" />
        <ViceOtoscope v-if="userDisplayName === 'Otoscope'" class="h-8 w-8" />
        <ViceCamera v-if="userDisplayName === 'External Camera'" class="h-10 w-10" />
        <ViceStethoscope v-if="userDisplayName === 'Stethoscope'" class="h-10 w-10" />
        <ViceDermatoscope v-if="userDisplayName === 'Dermatoscope'" class="h-8 w-8" />
      </div>
      <span class="tooltip-text ml-2">{{ userDisplayName }}</span>
    </div>

    <!-- Snapshot Picture capturing -->
    <div class="absolute z-20 left-2 w-10 h-10 top-1/2 mt-negative-20px">
      <div
        v-if="supportSnapshots && isClinicianStream && sessionStore.snapshotsAllowed"
        class="rounded-full w-full h-full flex justify-around"
      >
        <SnapshotButton
          :jitsiId="userJitsiId"
          :sessionId="sessionStore.sessionDBId"
          :deviceName="userDisplayName"
          v-tippy="{
            content: 'Take Snapshot',
            theme: 'light-border',
            interactive: true,
          }"
        />
      </div>
    </div>

    <!-- Recording Timer -->
    <div
      class="absolute z-20 left-1/2 w-10 h-10 top-2 ml-negative-20px"
      :class="userDisplayName === 'Stethoscope' ? 'margin-timer-steth' : 'ml-negative-20px'"
    >
      <Timer v-if="streamRecording" :seconds="RECORDING_LIMIT_MINUTES * 60" />
    </div>

    <!-- Snapshot Video capturing -->
    <div
      v-if="isConsultationPhase && isClinicianStream"
      class="absolute z-30 right-2 w-10 h-10 top-1/2 mt-negative-20px"
    >
      <div
        v-show="supportRecording && sessionStore.videoSnapshotsAllowed"
        class="rounded-full w-full h-full flex justify-around"
      >
        <VideoSnapshotButton
          :jitsiId="userJitsiId"
          :deviceName="userDisplayName?.replace(/ /g, '_')"
          :sessionId="sessionStore.sessionDBId"
          :sessionUniqueId="sessionStore.sessionUniqueId"
          :clinicianId="sessionStore.clinicianId"
          @isRecording="handleRecording"
          @recording-error="onRecordingError"
          v-tippy="{
            content: streamRecording ? 'Stop recording' : 'Start recording',
            theme: 'light-border',
            interactive: true,
          }"
        />
      </div>
    </div>

    <!-- Tile -->
    <div
      class="block h-full relative rounded-2xl overflow-visible bg-vice-medium-teal"
      :class="{ 'ring-4 ring-vice-light-red rounded-2xl': streamRecording }"
    >
      <!-- Icon and trigger -->
      <div class="absolute top-0 right-0 z-10" v-if="isConsultationPhase && isClinicianStream">
        <div class="p-3 relative cursor-pointer" :class="{ 'sm:p-2': !enlargedMode }">
          <div class="relative flex items-center gap-2">
            <div
              data-cy="toggle-focus"
              :class="{ 'sm:p-1': !enlargedMode }"
              class="
                bg-vice-dark-grey bg-opacity-70
                text-white
                rounded-full
                flex
                justify-center
                items-center
                p-2
                hover:bg-opacity-90
              "
            >
              <ArrowsExpandIcon
                class="h-6 w-6"
                v-if="!enlargedMode"
                :class="{ 'sm:w-5 sm:h-5': !enlargedMode }"
                @click="onEnlargeIconClick(tileId)"
              />
              <ArrowsCollapseIcon class="p-0.5" @click="onMinimizeIconClick" v-else />
            </div>
          </div>
        </div>
      </div>

      <!-- Mute -->
      <ToggleStethoscopePlayback
        v-if="streamAvailable && userDisplayName === 'Stethoscope'"
        :isEnabled="!muteStethoscope"
        :isLoading="false"
        label="Playback"
        @state-toggled="togglePlayback"
        class="absolute left-12 px-2 py-2 z-10"
      />

      <ToggleMuteStream
        v-if="
          isClinicianStream &&
          streamAvailable &&
          sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION
        "
        :isStreamMuted="streamMuted"
        @stream-state-toggled="toggleStreamState"
      />

      <StreamPlayerRemote
        :jitsiUserId="userJitsiId"
        :setVideoAudioRefs="setVideoAudioRefs"
        :showVideo="shouldShowRemoteUserVideo(userJitsiId)"
        :showVideoMirrored="userDisplayName === 'External Camera'"
        :streamMuted="streamMuted"
        :isAudioMuted="
          (muteStethoscope && userDisplayName === 'Stethoscope') ||
          sessionStore.isPutOnHold ||
          isMultiCameraStream ||
          (sessionStore.isClinician && sessionStore.isPhasePaused)
        "
      />

      <div
        class="justify-center items-center h-full w-full rounded-2xl bg-vice-medium-teal"
        v-show="
          !shouldShowRemoteUserVideo(userJitsiId) &&
          !shouldShowRemoteUserVideoOff &&
          isClinicianStream &&
          userDisplayName !== 'Stethoscope'
        "
      >
        <!-- Other streams without video -->
        <div class="flex justify-center h-full items-center">
          <ViceDermatoscope v-if="userDisplayName === 'Dermatoscope'" class="h-20 w-20" />
          <ViceExamCam v-if="userDisplayName === 'Exam Camera'" class="h-20 w-20" />
          <ViceOtoscope v-if="userDisplayName === 'Otoscope'" class="h-20 w-20" />
          <ViceMonitor v-if="userDisplayName === 'Monitor'" class="h-20 w-20" />
          <ViceCamera v-if="userDisplayName === 'External Camera'" class="h-20 w-20" />
        </div>
      </div>

      <span
        v-if="streamMuted && !sessionStore.isPhasePaused"
        class="muted-text w-full items-center justify-center rounded-2xl"
        :class="{ 'bg-gray-500 opacity-50': userDisplayName === 'Stethoscope' }"
      />
      <span
        v-if="streamMuted && !sessionStore.isPhasePaused"
        class="muted-text w-full items-center justify-center rounded-2xl"
      >
        PAUSED
      </span>
      <div
        class="flex justify-center items-center h-full w-full bg-vice-medium-teal rounded-2xl"
        v-show="shouldShowRemoteUserVideoOff"
      >
        <VideoOff
          v-if="sessionStore.currentPhase !== VICE_SESSION_PHASES.CONSULTATION"
          :name="userDisplayName"
          :profile_photo_url="remoteDisplayInfo?.['avatar']"
          class="rounded-2xl block overflow-hidden relative"
        />
        <template v-else-if="isExternalDevice">
          <StethoscopeAudioVisualizer
            v-if="
              streamAvailable && userDisplayName === 'Stethoscope' && !sessionStore.isPhasePaused
            "
            :stream="remoteDisplayInfo.tracks[0].stream"
            :muted="muteStethoscope"
          />
          <div v-else class="flex justify-center h-full items-center">
            <ViceStethoscope v-if="userDisplayName === 'Stethoscope'" class="h-20 w-20" />
          </div>
        </template>
        <template v-else>
          <UserTurnedStreamOffTile
            v-if="!isMultiCameraStream"
            :profile_photo_url="remoteDisplayInfo?.['avatar']"
            :name="userDisplayName"
            :remoteStats="remoteDisplayInfo.connectionQuality"
            :minimised="minimised"
          />

          <VideoOff
            :name="remoteDisplayInfo?.['name']"
            :profile_photo_url="remoteDisplayInfo?.['avatar']"
            :showImage="false"
            displayText="Stream preview is hidden"
            v-else
          />
        </template>
      </div>

      <!-- Raised hand border -->
      <div
        v-if="sessionStore.isHandRaised(remoteDisplayInfo?.userId)"
        class="absolute top-0 w-full h-full border-4 border-yellow rounded-xl"
      />

      <SpeakingIndicator
        v-if="userDisplayName !== 'Stethoscope'"
        :audioLevel="remoteDisplayInfo.audioLevel"
      />

      <UserLabel
        :class="{ 'text-xxs': enlargedJitsiId }"
        :isMutedMicrophone="!remoteDisplayInfo?.['audio'] || sessionStore.isPutOnHold"
        :displayName="userDisplayName"
        :raisedHand="sessionStore.isHandRaised(remoteDisplayInfo?.userId)"
        :isExternalDevice="isExternalDevice"
        :isMultiCameraStream="isMultiCameraStream"
        :localStats="remoteDisplayInfo?.['connectionQuality']"
        @togglePreviewOverlay="toggleMultiCamera(userJitsiId)"
        @disconnectDevice="disconnectMultiCamera(userJitsiId)"
        @changeCameraSource="switchMultiCamera(userJitsiId)"
        :shouldShowRemoteUserVideoOff="shouldShowRemoteUserVideoOff"
      />
    </div>
  </li>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { directive } from 'vue-tippy';
import { ArrowsExpandIcon } from '@heroicons/vue/outline';
import VideoOff from '@/Components/Session/Grid/VideoOff.vue';
import UserLabel from '@/Components/Session/Grid/UserLabel.vue';
import useSessionStore from '@/Stores/useSessionStore';
import {
  VICE_SESSION_PHASES,
  EXTERNAL_CONNECTED_VIDEO_DEVICES,
  EXTERNAL_CONNECTED_AUDIO_DEVICES,
  CAMERA_TYPES,
  OTOSCOPE_LABELS,
  DERMATOSCOPE_LABELS,
  MONITOR_LABELS,
  SUPPORTS_SNAPSHOT,
  SUPPORTS_RECORDING,
  RECORDING_LIMIT_MINUTES,
  EXAM_CAM_LABELS,
} from '@/Utils/vice-constants';
import StreamPlayerRemote from '@/Components/Session/Grid/StreamPlayerRemote.vue';
import SpeakingIndicator from '@/Components/Session/Grid/SpeakingIndicator';
import StethoscopeAudioVisualizer from '@/Components/Session/Grid/StethoscopeAudioVisualizer.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import UserTurnedStreamOffTile from '@/Components/Session/Grid/UserTurnedStreamOffTile';
import ArrowsCollapseIcon from '@/Components/Icons/ArrowsCollapseIcon.vue';
import ToggleStethoscopePlayback from '@/Components/Inputs/ToggleStethoscopePlayback.vue';
import ToggleMuteStream from '@/Components/Inputs/ToggleMuteStream.vue';
import ViceMonitor from '@/Components/Icons/ViceMonitor.vue';
import ViceOtoscope from '@/Components/Icons/ViceOtoscope.vue';
import ViceStethoscope from '@/Components/Icons/ViceStethoscope.vue';
import ViceDermatoscope from '@/Components/Icons/ViceDermatoscope.vue';
import ViceCamera from '@/Components/Icons/ViceCamera.vue';
import SnapshotButton from '@/Components/Buttons/SnapshotButton.vue';
import VideoSnapshotButton from '@/Components/Buttons/VideoSnapshotButton.vue';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';
import Timer from '@/Components/Loaders/Timer.vue';
import ViceExamCam from '@/Components/Icons/ViceExamCam.vue';

export default {
  directives: {
    tippy: directive,
  },
  props: {
    index: {
      type: [Number, String],
      required: true,
    },
    userJitsiId: {
      type: [Number, String],
    },
    enlargedJitsiId: {
      type: String,
      default: null,
    },
    remoteDisplayInfo: {
      type: Object,
      required: true,
    },
    shouldShowRemoteUserVideo: {
      type: Function,
      required: true,
    },
    muteMedia: {
      type: Function,
    },
    setVideoAudioRefs: {
      type: Function,
      required: true,
    },
    moderatorId: {
      type: [Number, String],
    },
    vuzixStreamId: {
      type: [Number, String],
    },
    tileId: {
      type: String,
      required: false,
    },
    isClinicianStream: {
      type: Boolean,
      required: true,
    },
    gridDimension: {
      type: Object,
      required: true,
    },
  },
  emits: ['toggleEnlargeMode'],
  components: {
    ViceExamCam,
    Timer,
    VideoSnapshotButton,
    VideoOff,
    UserLabel,
    ViceCamera,
    ViceMonitor,
    ViceOtoscope,
    SnapshotButton,
    ViceStethoscope,
    ViceDermatoscope,
    ToggleMuteStream,
    SpeakingIndicator,
    StreamPlayerRemote,
    UserTurnedStreamOffTile,
    ToggleStethoscopePlayback,
    StethoscopeAudioVisualizer,
    ArrowsExpandIcon,
    ArrowsCollapseIcon,
  },

  setup(props, { emit }) {
    const sessionStore = useSessionStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const streamMuted = ref(false);
    const streamRecording = ref(false);
    const muteStethoscope = ref(false);
    const isConsultationPhase = ref(sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION);

    const gridIndex = computed(() => (props.index < 20 ? props.index : 20));
    const enlargedMode = computed(() =>
      Boolean(props.enlargedJitsiId === props.userJitsiId && props.userJitsiId !== null),
    );
    const minimised = computed(() =>
      Boolean(props.enlargedJitsiId !== null && props.enlargedJitsiId !== props.userJitsiId),
    );
    const streamAvailable = computed(() => !!props.remoteDisplayInfo?.tracks[0]?.stream);
    const tileContainerClass = computed(
      () =>
        `col-start-${props.gridDimension[gridIndex.value].start} row-start-${
          props.gridDimension[gridIndex.value].row_start
        }  col-span-${props.gridDimension[gridIndex.value].cols} ${
          props.gridDimension[gridIndex.value].hidden ? 'hidden' : ''
        } row-span-${props.gridDimension[gridIndex.value].rows}`,
    );
    const enlargedContainerClass = 'col-start-2 col-span-10 row-span-8 row-start-1';

    const externalCameraJitsiIds = computed(() =>
      Object.keys(multiCameraDevicesStore.allCameraTypes).map((cameraType) =>
        multiCameraDevicesStore.allCameraTypes[cameraType].jitsiRoom?.myUserId(),
      ),
    );

    const isMultiCameraStream = computed(() =>
      externalCameraJitsiIds.value.includes(props.userJitsiId),
    );

    const isExternalCameraConnected = computed(
      () =>
        !!multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find(
          (cam) =>
            cam.deviceId ===
            multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.EXTERNAL_WEBCAM]?.cameraDeviceId,
        ),
    );

    const externalConnectedDevices = computed(() =>
      Object.values(EXTERNAL_CONNECTED_VIDEO_DEVICES).concat(
        Object.values(EXTERNAL_CONNECTED_AUDIO_DEVICES),
      ),
    );

    const isExternalDevice = computed(() =>
      externalConnectedDevices.value.includes(props.remoteDisplayInfo.userId),
    );

    const isExternalVideoDevice = computed(() =>
      Object.values(EXTERNAL_CONNECTED_VIDEO_DEVICES).includes(props.remoteDisplayInfo.userId),
    );

    let deviceLabels = [];
    switch (props.remoteDisplayInfo.userId) {
      case EXTERNAL_CONNECTED_VIDEO_DEVICES.OTOSCOPE:
        deviceLabels = OTOSCOPE_LABELS;
        break;
      case EXTERNAL_CONNECTED_VIDEO_DEVICES.DERMATOSCOPE:
        deviceLabels = DERMATOSCOPE_LABELS;
        break;
      case EXTERNAL_CONNECTED_VIDEO_DEVICES.EXAM_CAM:
        deviceLabels = EXAM_CAM_LABELS;
        break;
      case EXTERNAL_CONNECTED_VIDEO_DEVICES.MONITOR:
        deviceLabels = MONITOR_LABELS;
        break;
      default:
    }

    const isVideoDeviceConnected = computed(
      () =>
        !!multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find((device) =>
          deviceLabels.some((singleLabel) => device.label.includes(singleLabel)),
        ),
    );

    const userDisplayName = computed(() => {
      if (isExternalDevice.value) {
        return props.remoteDisplayInfo.userId;
      }
      return isMultiCameraStream.value
        ? props.remoteDisplayInfo.userId
        : props.remoteDisplayInfo.name;
    });

    const supportSnapshots = computed(() => SUPPORTS_SNAPSHOT.includes(userDisplayName.value));
    const supportRecording = computed(() => SUPPORTS_RECORDING.includes(userDisplayName.value));

    const getActiveMultiCamData = (jitsiId) =>
      _.filter(
        multiCameraDevicesStore.allCameraTypes,
        (_props) => _props.jitsiRoom?.myUserId() === jitsiId,
      )[0];

    const disconnectMultiCamera = async (userId) => {
      const activeCamera = getActiveMultiCamData(userId);
      await multiCameraDevicesStore.removeCamera(activeCamera?.name, false);
    };

    const shouldShowRemoteUserVideoOff = computed(
      () =>
        !props.remoteDisplayInfo?.video ||
        props.remoteDisplayInfo?.isVideoHidden ||
        sessionStore.isPutOnHold,
    );

    // Toggle Display but keep video visible to attendees
    const toggleMultiCamera = () => {
      props.remoteDisplayInfo.isVideoHidden = !props.remoteDisplayInfo.isVideoHidden;
    };

    // During the live consultation, the external cameras act as remote users.
    // We therefore need to get the videoAudioRef and assign to our local store so that
    // we can attach and remove tracks.
    const switchMultiCamera = (userId) => {
      // Todo: access camera from the store
      const activeCamera = _.filter(
        multiCameraDevicesStore.allCameraTypes,
        (_props) => _props.jitsiRoom?.myUserId() === userId,
      )[0];

      multiCameraDevicesStore.setCurrentActiveCameraType(activeCamera.name);
      multiCameraDevicesStore.openAVSettingsModal();
    };

    const onRecordingError = (error) => {
      console.error('Recording error:', error);
      // ToDo: human-friendly error msg
    };

    const onEnlargeIconClick = () => {
      emit('toggleEnlargeMode', props.userJitsiId);
    };

    const onMinimizeIconClick = () => {
      emit('toggleEnlargeMode');
    };

    const handleRecording = (state) => {
      streamRecording.value = state;
    };

    if (isMultiCameraStream.value) {
      watch(isExternalCameraConnected, async (newVal, oldVal) => {
        if (!newVal && oldVal) {
          await disconnectMultiCamera(props.userJitsiId);
        }
      });
    }

    onUnmounted(async () => {
      if (enlargedMode.value) onMinimizeIconClick();
    });

    onMounted(async () => {
      if (
        props.isClinicianStream &&
        userDisplayName.value === EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE
      ) {
        toggleStreamState(true);
      }
    });

    if (isExternalVideoDevice.value) {
      watch(isVideoDeviceConnected, async (newVal, oldVal) => {
        if (!newVal && oldVal) {
          await multiCameraDevicesStore.removeExternalVideoDevice(
            props.remoteDisplayInfo.userId,
            false,
          );
        }
      });
    }

    const togglePlayback = () => {
      muteStethoscope.value = !muteStethoscope.value;
    };

    const toggleStreamState = (e) => {
      streamMuted.value = e;
      props.muteMedia(props.userJitsiId, e);
    };

    return {
      minimised,
      streamMuted,
      sessionStore,
      enlargedMode,
      streamRecording,
      streamAvailable,
      muteStethoscope,
      userDisplayName,
      isExternalDevice,
      supportSnapshots,
      supportRecording,
      tileContainerClass,
      isConsultationPhase,
      isMultiCameraStream,
      isExternalVideoDevice,
      enlargedContainerClass,
      shouldShowRemoteUserVideoOff,
      VICE_SESSION_PHASES,
      RECORDING_LIMIT_MINUTES,
      togglePlayback,
      handleRecording,
      onRecordingError,
      switchMultiCamera,
      toggleStreamState,
      toggleMultiCamera,
      onEnlargeIconClick,
      onMinimizeIconClick,
      disconnectMultiCamera,
    };
  },
};
</script>
<style scoped></style>
